import { useHistory, useLocation, useParams } from 'react-router';
import { useComponentSectionContext } from '../context/useComponentSectionContext';
import { useControlActionModalContent } from '../useControlActionModalContent';
import { useDatasetContext } from '../context/useDatasetContext';
import { useSlaveDatasetContext } from '../context/useSlaveDatasetContext';
import { useFormTokenChainContext } from '../context/useFormTokenChainContext';
import { useSelectedRowsContext } from '../context/useSelectedRowsContext';
import useRoutes from '../useRoutes';
import { getMethodTypeCallback } from './utils/callbackTypes/getMethodTypeCallback';
import { getFormTypeCallback } from './utils/callbackTypes/getFormTypeCallback';
import { getAggregateTypeCallback } from './utils/callbackTypes/getAggregateTypeCallback';
import { getDatasetTypeCallback } from './utils/callbackTypes/getDatasetTypeCallback';
import { getLayoutsTypeCallback } from './utils/callbackTypes/getLayoutsTypeCallback';
import { getAppTypeCallback } from './utils/callbackTypes/getAppTypeCallback';
import { getPrintTypeCallback } from './utils/callbackTypes/getPrintTypeCallback';
import { guardControlAction } from './utils/guardControlAction';
import {
  AggregateControlAction,
  Dataset,
  DatasetControl,
  GlobalLocationStateType,
} from '../../types/types';
import { useDefaultNumberFormat } from '../../api/hooks/useDefaultNumberFormat';
import { useUserState } from '../../hooks/context/useUserState';
import { useMemo, useState } from 'react';
import useApiErrorHandling from '../useApiErrorHandling';
import { useDatasetData } from '../../api/hooks/useDatasetData';
import { getDataParamsString } from '../../utils/apiRequests/getDataParamStrings';
import { apiRequest } from '../../api/utils/apiRequest';
import { getDatasetDataApiRequestData } from '../../api/utils/requestData/getDatasetDataApiRequestData';
import { getSubsetDataApiRequestData } from '../../api/utils/requestData/getSubsetDataApiRequestData';
import { getFilterParamObject } from '../../utils/apiRequests/getFilterParamObject';
import { stripEmptyActiveFilters } from '../../utils/apiRequests/stripEmptyActiveFilters';
import { appendDatasetControlDataParam } from '../../utils/datasetControl/appendDatasetControlDataParam';
import { getControlActionItems } from '../../utils/apiRequests/getControlActionItems';
import { getDataKey } from '../../utils/getDataKey';
import { resolveTemplate } from '../../utils/json/resolveTemplate';
import { useDatasetSessionStorageContext } from '../context/useDatasetSessionStorageContext';
import { useResponseActionsContext } from '../context/useResponseActionsContext';
import { getDataFromDatasetRequest } from '../../api/utils/getDataFromDatasetRequest';
import { computeDeselectedIdsFromSelectionMode } from '../../components/Manager/DatasetButtons/utils/computeDeselectedIdsFromSelectionMode';
import { debugLogger } from '../../debug/DebugLogger';

const log = debugLogger.scope('controlActions');

export const useControlAction = () => {
  const {
    state: { token },
  } = useUserState();
  const {
    open,
    openAggregateResultModal,
    openPrintTemplateModal,
    close,
    isModalOpen,
    modalProps,
    setModalLoading,
  } = useControlActionModalContent();
  const { createRootToken, getActualTokenFromUrl, createNestedToken } =
    useFormTokenChainContext();
  const { componentSection } = useComponentSectionContext();
  const {
    slaveTabIndex,
    dataset: rootDataset,
    cachedQueryIdRef,
    datasetControl,
    slaveDatasets,
    datasetId: contextDatasetId,
  } = useDatasetContext();
  const { cachedQueryId: slaveCachedQueryId, subsetId: slaveContextSubsetId } =
    useSlaveDatasetContext();
  const {
    expandedRow: masterExpandedRow,
    selectedRows,
    selectedSlaveRows,
    selectedVirtualRows: selectedMasterVirtualRows,
    selectedSlaveVirtualRows,
    deselectedRowIds,
    deselectedSlaveRowIds,
    setReloadLocateInProgress,
    setSlaveReloadLocateInProgress,
  } = useSelectedRowsContext();
  const { fromItemExternalUrl } = useDatasetSessionStorageContext();
  const {
    pathname,
    search,
    state: locationState,
  } = useLocation<GlobalLocationStateType>();
  // Session flag clears once the external detail loads; the redirect also stamps
  // location state so a form opened from that detail still counts as external.
  const enteredFromExternalUrl =
    fromItemExternalUrl || Boolean(locationState?.enteredFromExternalUrl);
  const { itemId, datasetId, subsetId, referenceDatasetId } = useParams<{
    itemId?: string;
    datasetId?: string;
    subsetId?: string;
    referenceDatasetId?: string;
  }>();
  const history = useHistory();
  const routes = useRoutes();
  const handleApiError = useApiErrorHandling();
  const { resolveDefaultNumberFormat } = useDefaultNumberFormat();
  const { getResponseActionHandler } = useResponseActionsContext();

  const isSlaveDataset = useMemo(
    () =>
      componentSection === 'list-subset' ||
      componentSection === 'detail-subset',
    [componentSection]
  );

  const masterDataKey = getDataKey(rootDataset);

  const { fetch: fetchData, detailedFetch: detailedFetchData } = useDatasetData(
    {
      rethrowErrors: true,
    }
  );
  // Tracks the prefetch of missing virtual rows (fetchNeededDatasetData).
  // Used to render a full-screen <Loading/> while the bulk fetch runs.
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);

  /* Creates the action function for the control button */
  const getControlAction = (
    activeFilters: Record<string, unknown> | null,
    activeSorting: any[],
    dataset: Dataset,
    data: any[],
    totalCount: number | undefined,
    control: DatasetControl,
    rowsToUse: any[],
    currentRow: any,
    selectedVirtualRows: number,
    refetchData: () => void
  ) => {
    // Fetches dataset data — supports both string and DetailedApiMethod.
    // Toggles isActionLoading so consumers can render a global <Loading/> overlay
    // while the (potentially heavy) prefetch of missing virtual rows runs.
    // No offset/limit sent — API returns all rows without paging constraint
    const fetchNeededDatasetData = async () => {
      setIsActionLoading(true);
      try {
        // Dataset control data belongs to the master dataset only
        const requestDatasetControl =
          isSlaveDataset || datasetControl === null
            ? undefined
            : datasetControl;
        const filters = getFilterParamObject(activeFilters || {});
        // Prefetch only needs id column (for form/method request bodies)
        // and label column (for confirm-modal text); skip the full
        // visible-items set used by the grid.
        const controlActionItems = getControlActionItems(dataset);
        // No Query Cache for the all-rows prefetch — null drops the header
        const requestData = isSlaveDataset
          ? getSubsetDataApiRequestData(
              masterExpandedRow?.[masterDataKey],
              dataset,
              activeSorting || [],
              token,
              0,
              filters,
              undefined,
              null
            )
          : getDatasetDataApiRequestData(
              dataset,
              activeSorting || [],
              token,
              0,
              filters,
              undefined,
              null,
              undefined,
              undefined,
              requestDatasetControl
            );

        // Strip paging params — API returns all rows without offset/limit constraint
        delete requestData.limit;
        delete requestData.offset;
        // Override items built by getItemsParamArray with the minimal list.
        requestData.items = controlActionItems;

        // String apiMethod flow — build URL params and fetch directly
        if (typeof dataset.apiMethod === 'string') {
          const { apiUrl } = await resolveTemplate(
            { apiUrl: dataset.apiMethod },
            requestData
          );

          const paramsString = getDataParamsString(
            activeFilters || [],
            activeSorting || {},
            dataset,
            undefined,
            undefined,
            controlActionItems
          );

          return await getDataFromDatasetRequest(() =>
            fetchData(`${apiUrl}${paramsString}`, dataset, false, undefined)
          );
        }

        // DetailedApiMethod flow — build request data and resolve templates
        return await apiRequest(dataset.apiMethod, requestData, (apiMethod) =>
          getDataFromDatasetRequest(() => detailedFetchData(apiMethod, dataset))
        );
      } finally {
        // Always clear loading — errors propagate to caller's try/catch
        setIsActionLoading(false);
      }
    };

    const type = control.action.type;

    // Rows unchecked after a select-all. Gated by selection mode for the same
    // reason as the virtual count — 'dontcare_useall' means the whole filter.
    const resolvedDeselectedRowIds = computeDeselectedIdsFromSelectionMode(
      isSlaveDataset ? deselectedSlaveRowIds : deselectedRowIds,
      control.selectionMode
    );

    // Logs at invocation (click), not at callback build time.
    const withStartLog =
      (action: () => Promise<unknown>) => (): Promise<unknown> => {
        /** @debugFeature controlActions Logs a started control action: type, control label, action definition, dataset, section and selection counts. */
        log('control action started', () => ({
          type,
          controlLabel: control.label,
          action: control.action,
          datasetId: dataset.datasetId,
          componentSection,
          rowCount: rowsToUse.length,
          selectedVirtualRows,
          deselectedRowIds: resolvedDeselectedRowIds,
        }));
        return action();
      };

    // External flow returns to list-mode item URL. Preserve subset segment
    // if user opened the form from a subset-selected detail page so close
    // lands on /item/X/subset/Y instead of dropping the subset.
    const resolveExternalFromUrl = () => {
      if (!enteredFromExternalUrl || !datasetId || !itemId) return pathname;
      if (subsetId) return routes.subset(datasetId, itemId, subsetId);
      return routes.subsetDetail(datasetId, itemId);
    };

    // Keep the dataset control data param on the return URL — a form stores
    // it in its token's fromUrl, so closing the form (even after F5 inside it)
    // lands back on the dataset with the data still applied.
    const fromUrlResolved = appendDatasetControlDataParam(
      resolveExternalFromUrl(),
      search
    );

    if (type === 'form') {
      return withStartLog(
        guardControlAction(
          getFormTypeCallback(
            dataset,
            rowsToUse,
            selectedVirtualRows,
            resolvedDeselectedRowIds,
            control,
            componentSection,
            routes,
            rootDataset,
            slaveTabIndex,
            fromUrlResolved,
            enteredFromExternalUrl,
            masterExpandedRow,
            referenceDatasetId,
            itemId,
            history,
            token,
            fetchNeededDatasetData,
            getActualTokenFromUrl,
            createRootToken,
            createNestedToken,
            open,
            close,
            setModalLoading
          ),
          handleApiError
        )
      );
    }

    if (type === 'method') {
      return withStartLog(
        guardControlAction(
          getMethodTypeCallback(
            dataset,
            isSlaveDataset ? rootDataset : undefined,
            rowsToUse,
            currentRow,
            isSlaveDataset ? masterExpandedRow : undefined,
            selectedVirtualRows,
            resolvedDeselectedRowIds,
            control,
            token,
            isSlaveDataset ? slaveCachedQueryId : cachedQueryIdRef,
            isSlaveDataset,
            refetchData,
            fetchNeededDatasetData,
            open,
            close,
            setModalLoading,
            handleApiError,
            getResponseActionHandler
          ),
          handleApiError
        )
      );
    }

    // Standard request data of the source grid — the base of the template
    // resolve context of 'dataset' and 'app' actions ({{ filters }},
    // {{ master.id }}, chained {{ datasetControl }} all work like in apiMethod
    // templates).
    const buildActionTemplateRequestData = () => {
      const templateFilters = getFilterParamObject(
        stripEmptyActiveFilters(activeFilters || {})
      );
      return isSlaveDataset
        ? getSubsetDataApiRequestData(
            masterExpandedRow?.[masterDataKey],
            dataset,
            activeSorting || [],
            token,
            0,
            templateFilters,
            undefined,
            null,
            undefined,
            masterDataKey
          )
        : getDatasetDataApiRequestData(
            dataset,
            activeSorting || [],
            token,
            0,
            templateFilters,
            undefined,
            null,
            undefined,
            undefined,
            datasetControl === null ? undefined : datasetControl
          );
    };

    if (type === 'dataset') {
      const templateRequestData = buildActionTemplateRequestData();

      return withStartLog(
        guardControlAction(
          getDatasetTypeCallback(
            dataset,
            rowsToUse,
            currentRow,
            selectedVirtualRows,
            resolvedDeselectedRowIds,
            control,
            routes,
            history,
            fromUrlResolved,
            templateRequestData,
            fetchNeededDatasetData,
            open,
            close,
            setModalLoading
          ),
          handleApiError
        )
      );
    }

    if (type === 'aggregate') {
      const effectiveSelectedRows = isSlaveDataset
        ? selectedSlaveRows
        : selectedRows;
      const effectiveSelectedVirtualRows = isSlaveDataset
        ? selectedSlaveVirtualRows
        : selectedMasterVirtualRows;
      const isForSelectedRows =
        effectiveSelectedRows.length > 0 || effectiveSelectedVirtualRows > 0;
      return withStartLog(
        guardControlAction(
          getAggregateTypeCallback(
            dataset,
            control,
            token,
            activeFilters,
            activeSorting,
            data,
            effectiveSelectedRows,
            effectiveSelectedVirtualRows,
            resolvedDeselectedRowIds,
            isSlaveDataset,
            isSlaveDataset ? masterExpandedRow : undefined,
            masterDataKey,
            isSlaveDataset ? slaveCachedQueryId : cachedQueryIdRef,
            handleApiError,
            getResponseActionHandler,
            async (items) => {
              // Primed before the modal mounts so it renders formatted right away.
              // Gated on the definition only — the response format is unknown here.
              const usesDefaultFormat = (
                control.action as AggregateControlAction
              ).items.some(
                (item) =>
                  item.operation !== 'count' && !item.format && !item.formatItem
              );
              if (usesDefaultFormat) await resolveDefaultNumberFormat();
              openAggregateResultModal(control, items, isForSelectedRows);
            },
            isSlaveDataset
              ? setSlaveReloadLocateInProgress
              : setReloadLocateInProgress,
            isSlaveDataset || datasetControl === null
              ? undefined
              : datasetControl
          ),
          handleApiError
        )
      );
    }

    if (type === 'app') {
      return withStartLog(
        guardControlAction(
          getAppTypeCallback(
            dataset,
            rowsToUse,
            currentRow,
            selectedVirtualRows,
            resolvedDeselectedRowIds,
            control,
            contextDatasetId ?? datasetId,
            routes,
            history,
            fromUrlResolved,
            buildActionTemplateRequestData,
            fetchNeededDatasetData,
            isSlaveDataset ? 'subset' : 'dataset',
            open,
            close,
            setModalLoading
          ),
          handleApiError
        )
      );
    }

    if (type === 'layouts') {
      return withStartLog(
        guardControlAction(
          getLayoutsTypeCallback(
            control,
            currentRow,
            rowsToUse,
            contextDatasetId,
            history,
            routes
          ),
          handleApiError
        )
      );
    }

    if (type === 'print') {
      return withStartLog(
        guardControlAction(
          getPrintTypeCallback(
            control,
            dataset,
            activeFilters,
            activeSorting || [],
            token,
            routes,
            history,
            openPrintTemplateModal,
            close,
            setModalLoading,
            isSlaveDataset &&
              rootDataset &&
              masterExpandedRow &&
              (slaveContextSubsetId || subsetId || dataset.datasetId)
              ? {
                  parentDataset: rootDataset,
                  parentRow: masterExpandedRow,
                  parentIdItem: masterDataKey,
                  subsetId: String(
                    slaveContextSubsetId || subsetId || dataset.datasetId
                  ),
                  returnPath: pathname,
                }
              : undefined
          ),
          handleApiError
        )
      );
    }

    /** @debugFeature controlActions Logs an attempt to run a control action whose type has no handler. */
    return () =>
      log('unhandled control action type', () => ({
        type,
        controlLabel: control.label,
        action: control.action,
        datasetId: dataset.datasetId,
        componentSection,
      }));
  };

  return {
    getControlAction,
    isActionLoading,
    controlModal: {
      isModalOpen,
      modalProps,
    },
  };
};
